import { ContentLoading } from "../components/ContentLoading";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings2, UserRoundX } from "lucide-react";
import { useState } from "react";

import type { OpsEmployeeDirectoryItem, OpsRoleBundle } from "../../shared/operations-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { CellTitle, RowTable, type RowColumn } from "../components/ui/row-table";
import { Sheet, SheetBody, SheetFooter, SheetForm } from "../components/ui/sheet";
import { updateEmployeeRoles, updateEmployeeStatus } from "./api";
import { EmployeeCustomerAccessView } from "./EmployeeCustomerRoleEditor";
import { EmployeeRoleOptions } from "./EmployeeRoleOptions";
import { abilitiesByArea } from "./permission-language";

function RoleAssignmentList({
  employee,
  roleBundles,
}: {
  employee: OpsEmployeeDirectoryItem;
  roleBundles: OpsRoleBundle[];
}) {
  const roles = new Map(roleBundles.map((role) => [role.key, role]));
  if (employee.roleAssignments.length === 0) {
    return <p className="text-muted-foreground">No roles yet, so they can’t do anything.</p>;
  }
  return (
    <ul className="divide-y divide-muted" aria-label={`${employee.displayName}’s roles`}>
      {employee.roleAssignments.map((assignment) => {
        const role = roles.get(assignment.roleKey);
        return (
          <li
            className="flex flex-wrap items-baseline justify-between gap-x-4 py-2.5"
            key={`${assignment.roleKey}:${assignment.organizationId ?? "global"}`}
          >
            <span className="font-semibold">
              {role?.displayName ?? "Retired role"}
              {role?.status === "disabled" ? (
                <Badge className="ml-2" variant="secondary">
                  Turned off
                </Badge>
              ) : null}
            </span>
            <span className="text-muted-foreground">
              {assignment.organizationName ?? "All customers"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function RolesView({
  employee,
  roleBundles,
  onBack,
}: {
  employee: OpsEmployeeDirectoryItem;
  roleBundles: OpsRoleBundle[];
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>(employee.globalRoleKeys);
  const mutation = useMutation({
    mutationFn: () => updateEmployeeRoles(employee.id, selected),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "employees"] });
      onBack();
    },
  });
  return (
    <SheetForm
      aria-label="Roles for every customer"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <SheetBody>
        <p className="text-muted-foreground">
          These roles apply to every customer. Roles for single customers stay as they are.
        </p>
        <EmployeeRoleOptions
          onChange={(roleKey, checked) => {
            setSelected((current) =>
              checked ? [...current, roleKey].sort() : current.filter((key) => key !== roleKey),
            );
          }}
          roles={roleBundles.filter((role) => role.status === "active")}
          selected={selected}
        />
        {mutation.error ? <FieldError>{mutation.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="ghost" onClick={onBack}>
          Cancel
        </Button>
        <Button disabled={mutation.isPending} type="submit">
          {mutation.isPending ? "Saving…" : "Save roles"}
        </Button>
      </SheetFooter>
    </SheetForm>
  );
}

function StatusView({
  employee,
  onBack,
}: {
  employee: OpsEmployeeDirectoryItem;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const disabling = employee.status === "active";
  const mutation = useMutation({
    mutationFn: () => updateEmployeeStatus(employee.id, disabling ? "disabled" : "active"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "employees"] });
      onBack();
    },
  });
  return (
    <>
      <SheetBody>
        <p>
          {disabling
            ? "Their access to the operations console stops immediately and they are signed out everywhere."
            : "They can sign in again with their existing Google-linked identity and assigned roles."}
        </p>
        {mutation.error ? <FieldError>{mutation.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="ghost" onClick={onBack}>
          Cancel
        </Button>
        <Button
          disabled={mutation.isPending}
          onClick={() => {
            mutation.mutate();
          }}
          type="button"
          variant={disabling ? "destructive" : "default"}
        >
          {mutation.isPending ? "Saving…" : disabling ? "Disable employee" : "Enable employee"}
        </Button>
      </SheetFooter>
    </>
  );
}

function roleSummary(employee: OpsEmployeeDirectoryItem, roleBundles: OpsRoleBundle[]) {
  const roles = new Map(roleBundles.map((role) => [role.key, role]));
  const names = [
    ...new Set(
      employee.roleAssignments.map(
        (assignment) => roles.get(assignment.roleKey)?.displayName ?? "Retired role",
      ),
    ),
  ];
  const first = roles.get(employee.roleAssignments[0]?.roleKey ?? "");
  return {
    title: names.join(", ") || "No role",
    hint: names.length === 1 ? first?.description : undefined,
  };
}

function customerSummary(employee: OpsEmployeeDirectoryItem) {
  if (employee.roleAssignments.some((assignment) => assignment.organizationId === null))
    return "All customers";
  const names = [
    ...new Set(employee.roleAssignments.map((assignment) => assignment.organizationName ?? "")),
  ].filter(Boolean);
  if (!names.length) return "None";
  return names.length > 2
    ? `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`
    : names.join(", ");
}

function employeeColumns(roleBundles: OpsRoleBundle[]): RowColumn<OpsEmployeeDirectoryItem>[] {
  return [
    {
      key: "person",
      header: "Person",
      cell: (employee) => <CellTitle title={employee.displayName} detail={employee.username} />,
    },
    {
      key: "role",
      header: "Role",
      cell: (employee) => {
        const summary = roleSummary(employee, roleBundles);
        return (
          <CellTitle
            title={<span className="font-normal">{summary.title}</span>}
            detail={summary.hint}
          />
        );
      },
    },
    {
      key: "customers",
      header: "Customers",
      className: "hidden md:table-cell text-muted-foreground",
      cell: customerSummary,
    },
    {
      key: "status",
      header: "Status",
      cell: (employee) => (
        <Badge variant={employee.status === "active" ? "success" : "secondary"}>
          {employee.status === "active" ? "Active" : "Turned off"}
        </Badge>
      ),
    },
  ];
}

type EmployeeView = "details" | "roles" | "customers" | "status";

function viewTitle(employee: OpsEmployeeDirectoryItem, view: EmployeeView) {
  if (view === "roles") return `Change ${employee.displayName}’s roles`;
  if (view === "customers") return `${employee.displayName}’s customer access`;
  if (view === "status")
    return `${employee.status === "active" ? "Disable" : "Enable"} ${employee.displayName}?`;
  return employee.displayName;
}

function EmployeeSheet({
  currentEmployeeId,
  employee,
  roleBundles,
  onClose,
}: {
  currentEmployeeId: string;
  employee: OpsEmployeeDirectoryItem | null;
  roleBundles: OpsRoleBundle[];
  onClose: () => void;
}) {
  const [view, setView] = useState<EmployeeView>("details");
  const close = () => {
    setView("details");
    onClose();
  };
  return (
    <Sheet
      open={employee !== null}
      onClose={close}
      kicker={
        employee
          ? `${employee.username} · ${employee.status === "active" ? "Active" : "Turned off"}`
          : undefined
      }
      title={employee ? viewTitle(employee, view) : ""}
    >
      {employee ? (
        <EmployeeSheetContent
          key={employee.id}
          employee={employee}
          roleBundles={roleBundles}
          self={employee.id === currentEmployeeId}
          view={view}
          setView={setView}
          onDone={close}
        />
      ) : (
        <>
          <SheetBody />
          <SheetFooter />
        </>
      )}
    </Sheet>
  );
}

function EmployeeSheetContent({
  employee,
  roleBundles,
  self,
  view,
  setView,
  onDone,
}: {
  employee: OpsEmployeeDirectoryItem;
  roleBundles: OpsRoleBundle[];
  self: boolean;
  view: EmployeeView;
  setView: (view: EmployeeView) => void;
  onDone: () => void;
}) {
  const back = () => {
    setView("details");
  };
  if (view === "roles")
    return <RolesView employee={employee} roleBundles={roleBundles} onBack={back} />;
  if (view === "customers")
    return (
      <EmployeeCustomerAccessView employee={employee} roleBundles={roleBundles} onBack={back} />
    );
  if (view === "status") return <StatusView employee={employee} onBack={back} />;
  return (
    <>
      <SheetBody>
        <section aria-label="Roles">
          <h3 className="mb-1 text-lg font-bold">Roles</h3>
          <RoleAssignmentList employee={employee} roleBundles={roleBundles} />
        </section>
        <EmployeeAbilities employee={employee} />
        {self ? (
          <p className="rounded-md bg-muted p-4 text-muted-foreground">
            This is you. Another administrator has to change your access.
          </p>
        ) : null}
      </SheetBody>
      {self ? (
        <SheetFooter>
          <Button variant="secondary" onClick={onDone}>
            Done
          </Button>
        </SheetFooter>
      ) : (
        <EmployeeActions employee={employee} setView={setView} />
      )}
    </>
  );
}

function EmployeeActions({
  employee,
  setView,
}: {
  employee: OpsEmployeeDirectoryItem;
  setView: (view: EmployeeView) => void;
}) {
  const disabling = employee.status === "active";
  return (
    <SheetFooter
      start={
        <Button
          aria-label={`${disabling ? "Disable" : "Enable"} ${employee.displayName}`}
          variant={disabling ? "ghost" : "secondary"}
          className={disabling ? "text-destructive" : undefined}
          onClick={() => {
            setView("status");
          }}
        >
          <UserRoundX /> {disabling ? "Disable" : "Enable"}
        </Button>
      }
    >
      <Button
        aria-label={`Edit customer access for ${employee.displayName}`}
        variant="secondary"
        onClick={() => {
          setView("customers");
        }}
      >
        Customer access
      </Button>
      <Button
        aria-label={`Change roles for ${employee.displayName}`}
        onClick={() => {
          setView("roles");
        }}
      >
        <Settings2 /> Change roles
      </Button>
    </SheetFooter>
  );
}

function EmployeeAbilities({ employee }: { employee: OpsEmployeeDirectoryItem }) {
  const groups = abilitiesByArea(employee.permissions);
  return (
    <section aria-label={`What ${employee.displayName} can do`}>
      <h3 className="mb-1 text-lg font-bold">What {employee.displayName} can do</h3>
      {groups.length ? (
        <dl className="m-0 divide-y divide-muted">
          {groups.map((group) => (
            <div key={group.area} className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 py-2.5">
              <dt className="font-semibold">{group.area}</dt>
              <dd className="m-0 text-muted-foreground">{group.abilities.join("; ")}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-muted-foreground">Nothing across all customers.</p>
      )}
      <p className="mt-2 text-sm text-muted-foreground">
        Includes every role that applies to all customers and any temporary access. Roles for a
        single customer add to this for that customer only.
      </p>
      <details className="mt-3">
        <summary className="flex min-h-11 cursor-pointer items-center font-semibold text-link">
          Show technical permission names (for audits)
        </summary>
        <p className="font-mono text-sm leading-relaxed text-muted-foreground">
          {employee.permissions.join(" · ") || "None"}
        </p>
      </details>
    </section>
  );
}

export function EmployeeResults({
  currentEmployeeId,
  fetching,
  loading,
  query,
  roleBundles,
  rows,
}: {
  currentEmployeeId: string;
  fetching: boolean;
  loading: boolean;
  query: string;
  roleBundles: OpsRoleBundle[];
  rows: OpsEmployeeDirectoryItem[];
}) {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <div aria-busy={fetching} className="mt-3">
      {loading ? (
        <ContentLoading label="Loading employee list…" />
      ) : (
        <RowTable
          label="Employees"
          columns={employeeColumns(roleBundles)}
          rows={rows}
          rowKey={(employee) => employee.id}
          rowLabel={(employee) => employee.displayName}
          selectedKey={selected}
          onOpen={(employee) => {
            setSelected(employee.id);
          }}
          empty={query ? "No employees match your search." : "No employees yet."}
        />
      )}
      <EmployeeSheet
        currentEmployeeId={currentEmployeeId}
        employee={rows.find((employee) => employee.id === selected) ?? null}
        roleBundles={roleBundles}
        onClose={() => {
          setSelected(null);
        }}
      />
    </div>
  );
}
